import React from "react";
import styled, { css } from "styled-components";
import Link from "next/link";

import { ButtonVariants, ButtonSizes } from "./types";
import addHexAlpha from "../../utils/addHexAlpha";

interface ButtonProps {
  $variant: ButtonVariants;
  $size: ButtonSizes;
  $fullWidth: boolean;
  $iconOnly: boolean;
  $compact: boolean;
  $margin?: React.CSSProperties["margin"];
  $height?: React.CSSProperties["height"];
  $rounded: boolean;
  $underlined?: boolean;
}

const getFont = ({ $size }) => {
  if ($size === "xl" || $size === "l" || $size === "m") {
    return css`
      font-size: 16px;
      line-height: 1.5;
    `;
  } else if ($size === "m" || $size === "s") {
    return css`
      font-size: 14px;
      line-height: 1.4;
    `;
  }
  return css`
    font-size: 12px;
    line-height: 14px;
  `;
};

const OUTLINED_BUTTON_VARIANTS = ["secondary"];

export const buttonStyles = css<ButtonProps>`
  height: ${({ $height }) => $height || ""};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.DMSans};
  background-clip: padding-box;
  color: ${({ theme }) => theme.colors.primary};
  &:disabled {
    pointer-events: none;
    filter: grayscale(1);
  }
  ${({ $margin }) =>
    $margin &&
    css`
      margin: ${$margin};
    `}
  ${getFont};
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `};
  ${({ $size, $iconOnly, $compact, $variant }) => {
    const delta = OUTLINED_BUTTON_VARIANTS.includes($variant) ? -1 : 0;
    if ($size === "xl") {
      return css`
        padding: ${18 + delta}px ${32 + delta}px;
      `;
    } else if ($size === "l") {
      return css`
        padding: ${12 + delta}px ${32 + delta}px;
      `;
    } else if ($size === "m") {
      return css`
        padding: ${12 + delta}px ${24 + delta}px;
      `;
    } else if ($size === "s") {
      return css`
        padding: ${10.5 + delta}px ${12 + delta}px;
      `;
    } else if ($size === "xs") {
      if ($iconOnly) {
        return css`
          padding: ${4 + delta}px;
        `;
      } else if ($compact) {
        return css`
          padding: ${4 + delta}px ${12 + delta}px;
        `;
      }
      return css`
        padding: ${7 + delta}px ${10 + delta}px;
      `;
    } else if ($size === "xxs") {
      return css`
        height: fit-content;
        padding: ${5 + delta}px ${20 + delta}px;
      `;
    }
  }};
  ${({ $variant, $rounded, $underlined }) => {
    if ($variant === "primary") {
      return css`
        background: ${({ theme }) => theme.colors.primary};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.white};
        &:hover {
          hover: ${({ theme }) => theme.colors.white};
        }
        &:active {
          color: ${({ theme }) => theme.colors.adornment};
        }
      `;
    } else if ($variant === "blue") {
      return css`
        background: ${({ theme }) => theme.colors.blue};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.white};
        &:hover {
          color: ${({ theme }) => theme.colors.white};
        }
        &:active {
          color: rgba(255, 255, 255, 0.8);
        }
      `;
    } else if ($variant === "outlined-blue") {
      return css`
        border: 1px solid ${({ theme }) => theme.colors.blue};
        background: transparent;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.blue};
        &:hover {
          color: ${({ theme }) => theme.colors.blue};
        }
      `;
    } else if ($variant === "outlined-lightBlue") {
      return css`
        border: 1px solid ${({ theme }) => theme.colors.blue10};
        background: ${({ theme }) => theme.colors.white};
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.primary};
        &:hover {
          color: ${({ theme }) => theme.colors.primary};
        }
      `;
    } else if ($variant === "outlined-white") {
      return css`
        border: 1px solid ${({ theme }) => theme.colors.white};
        background: transparent;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.white};
        &:hover {
          color: ${({ theme }) => theme.colors.white};
        }
      `;
    } else if ($variant === "secondary") {
      return css`
        background: ${({ theme }) => theme.colors.white};
        border-radius: ${$rounded ? "1000px" : "4px"};
        border: 1px solid ${({ theme }) => theme.colors.graySolid6};
        &:hover {
          color: ${({ theme }) => theme.colors.primary};
          background: ${({ theme }) => theme.colors.graySolid1a};
          border-color: ${({ theme }) => theme.colors.primary};
        }
        &:active {
          background: ${({ theme }) => theme.colors.background};
        }
      `;
    } else if ($variant === "transparent-blue") {
      return css`
        background: transparent;
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.purpleBlue};
        &:hover {
          color: ${({ theme }) => theme.colors.purpleBlue};
        }
        &:active {
          background: ${({ theme }) => theme.colors.background};
        }
      `;
    } else if ($variant === "white-blue") {
      return css`
        background: ${({ theme }) => theme.colors.white};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.purpleBlue};
        &:hover {
          color: ${({ theme }) => theme.colors.purpleBlue};
        }
        &:active {
          background: ${({ theme }) => theme.colors.background};
        }
      `;
    } else if ($variant === "purple-blue") {
      return css`
        background: ${({ theme }) => theme.colors.purpleBlue};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.white};
        &:hover {
          color: ${({ theme }) => theme.colors.white};
        }
        &:active {
          color: rgba(255, 255, 255, 0.8);
        }
      `;
    } else if ($variant === "light-gray") {
      return css`
        background: ${({ theme }) => theme.colors.background};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.primary};
        &:hover {
          color: ${({ theme }) => theme.colors.primary};
        }
        &:active {
          color: ${({ theme }) => theme.colors.primary};
        }
      `;
    } else if ($variant === "danger") {
      return css`
        background: ${({ theme }) => theme.colors.red};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => addHexAlpha(theme.colors.white, 0.8)};
        &:hover {
          color: ${({ theme }) => theme.colors.white};
        }
        &:active {
          color: ${({ theme }) => theme.colors.white};
        }
      `;
    } else if ($variant === "lime-green") {
      return css`
        background: ${({ theme }) => theme.colors.green20};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => addHexAlpha(theme.colors.blue30, 0.8)};
        &:hover {
          color: ${({ theme }) => theme.colors.blue30};
        }
        &:active {
          color: ${({ theme }) => theme.colors.blue30};
        }
      `;
    } else if ($variant === "zircon") {
      return css`
        background: ${({ theme }) => theme.colors.zircon};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.blueRoyal};
        &:hover {
          color: ${({ theme }) => theme.colors.blueRoyal};
        }
        &:active {
          color: ${({ theme }) => theme.colors.blueRoyal};
        }
      `;
    } else if ($variant === "danger-light") {
      return css`
        background: ${({ theme }) => theme.colors.fairPink};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.red20};
        &:hover {
          color: ${({ theme }) => theme.colors.red20};
        }
        &:active {
          color: ${({ theme }) => theme.colors.red20};
        }
      `;
    } else if ($variant === "lightBlue") {
      return css`
        background: ${({ theme }) => theme.colors.blue10};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.blue};
        &:hover {
          color: ${({ theme }) => theme.colors.blue};
        }
        &:active {
          color: ${({ theme }) => theme.colors.blue};
        }
      `;
    } else if ($variant == "transparent") {
      return css`
        background: transparent;
        border: none;
        color: ${({ theme }) => theme.colors.purpleBlue};
        text-decoration: ${() => ($underlined ? "underline" : "none")};
        padding: 0;
        margin: 0;
        opacity: 0.8;
        &:hover {
          opacity: 1;
        }
      `;
    } else if ($variant === "algaeGreen") {
      return css`
        background: ${({ theme }) => theme.colors.algaeGreen};
        border: none;
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.primary};
        &:hover {
          color: ${({ theme }) => theme.colors.primary};
        }
        &:active {
          color: ${({ theme }) => theme.colors.primary};
        }
      `;
    } else if ($variant === "warning") {
      return css`
        background: ${({ theme }) => theme.colors.orange100};
        border: ${({ theme }) => `1px solid ${theme.colors.orange300}`};
        border-radius: ${$rounded ? "1000px" : "4px"};
        color: ${({ theme }) => theme.colors.bronze2};
        &:hover {
          color: ${({ theme }) => theme.colors.bronze2};
        }
        &:active {
          color: ${({ theme }) => theme.colors.bronze2};
        }
      `;
    }
  }};
`;

export const StyledAnchor = styled(Link)<ButtonProps>`
  ${buttonStyles};
`;

export const StyledExternalAnchor = styled.a<ButtonProps>`
  ${buttonStyles};
`;

export const LoadingWrapper = styled.span`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -250%, 0);
  opacity: 0;
`;

export const DoneWrapper = styled.span`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, 150%, 0);
  opacity: 0;
`;

export const StyledButton = styled.button<ButtonProps>`
  position: relative;
  overflow: hidden;
  ${buttonStyles}

  & > * {
    transition:
      transform 150ms ease-in-out,
      opacity 150ms ease-in-out;
  }

  &[data-loading="true"] > *:not([data-ui]),
  &[data-done="true"] > *:not([data-ui]) {
    transform: translate3d(0, 200%, 0);
    opacity: 0;
  }

  &[data-loading="true"] {
    ${LoadingWrapper} {
      transform: translate3d(-50%, -50%, 0);
      opacity: 1;
    }
  }

  &[data-done="true"] {
    ${DoneWrapper} {
      transform: translate3d(-50%, -50%, 0);
      opacity: 1;
    }
  }
`;
